import { Color3, InstancedMesh, Mesh, MeshBuilder, PBRMaterial, Quaternion, Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { ObstacleDistributionConfig } from '../ObstacleGenerator';
import { PlayerCollision } from '../PlayerCollision';
import { ObstacleBehavior } from './ObstacleBehavior';

/**
 * Spike Ring Behavior – spawns N spike instances in a ring that rotates continuously.
 * Uses instanced meshes for performance: one source mesh + material created statically.
 *
 * Config fields:
 *   ringRadius   – radius of the spike ring (default 3)
 *   radius       – number of spikes (reused, default 6)
 *   speed        – rotation speed in radians/sec (default 1.5)
 *   axis         – rotation axis: 'x' | 'y' | 'z' (default 'y')
 */
export class ObstacleSpikeRingBehavior extends ObstacleBehavior {
  /** Shared source mesh – created once, never rendered itself (isVisible = false) */
  static sourceMesh: Mesh;
  static spikeMaterial: PBRMaterial;

  private angle: number = 0;
  private spikeInstances: InstancedMesh[] = [];
  private spikeCount: number = 6;

  constructor(startPosition: Vector3, pathIndex: number, config: ObstacleDistributionConfig) {
    super('obstacleSpikeRing', startPosition, pathIndex, config);
  }

  static dispose(): void {
    if (ObstacleSpikeRingBehavior.sourceMesh) {
      ObstacleSpikeRingBehavior.sourceMesh.dispose();
      ObstacleSpikeRingBehavior.sourceMesh = null;
    }
    if (ObstacleSpikeRingBehavior.spikeMaterial) {
      ObstacleSpikeRingBehavior.spikeMaterial.dispose();
      ObstacleSpikeRingBehavior.spikeMaterial = null;
    }
  }

  initLevel(level: Level): ObstacleSpikeRingBehavior {
    super.initLevel(level);
    this.spikeCount = Math.round(this.config.radius ?? 6);
    const ringRadius = this.config.ringRadius ?? 6;

    // ── Create shared source mesh + material once ────────────────────────────
    if (!ObstacleSpikeRingBehavior.spikeMaterial) {
      const mat = this.level.game.materialService.createPBRColorMaterial('spikeMat', new Color3(1, 0, 0), 1);
      mat.metallic = 0.9;
      mat.roughness = 0.1;
      ObstacleSpikeRingBehavior.spikeMaterial = mat;
    }

    if (!ObstacleSpikeRingBehavior.sourceMesh) {
      const src = MeshBuilder.CreateCylinder(
        'spikeRing_source',
        { height: 3, diameterTop: 0, diameterBottom: 0.75, tessellation: 6 },
        this.level.scene
      );
      src.material = ObstacleSpikeRingBehavior.spikeMaterial;
      src.isVisible = false; // never render the source directly
      src.isPickable = false;
      ObstacleSpikeRingBehavior.sourceMesh = src;
    }

    // ── Spawn one instance per spike ─────────────────────────────────────────
    for (let i = 0; i < this.spikeCount; i++) {
      const angle = (i / this.spikeCount) * Math.PI * 2;

      const instance = ObstacleSpikeRingBehavior.sourceMesh.createInstance(`spikeRing_spike_${i}`);
      instance.parent = this.mesh;

      const x = Math.cos(angle) * ringRadius;
      const z = Math.sin(angle) * ringRadius;
      instance.position.set(x, 0, z);

      // Orient tip outward.
      // Cone tip defaults to +Y. Needed direction: (cos angle, 0, sin angle).
      // Rotation axis = Cross(+Y, outwardDir) = (sin angle, 0, -cos angle), by PI/2.
      const rotAxis = new Vector3(Math.sin(angle), 0, -Math.cos(angle));
      instance.rotationQuaternion = Quaternion.RotationAxis(rotAxis, Math.PI / 2);

      instance.receiveShadows = true;
      this.level.addShadowCaster(instance);

      // Collision
      instance.metadata = {
        playerCollision: new PlayerCollision(false, (level, player, event) => {
          if (player.vulnerable && !level.game.config.godMode) {
            player.collide(event, 'Hit by spike!');
          }
        }),
      };
      this.level.player?.registerCollisionMesh(instance);

      this.spikeInstances.push(instance);
    }

    return this;
  }

  update(deltaTime: number): void {
    const speed = this.config.speed ?? 1.5;
    const angle = this.level.absoluteTime * speed;

    switch (this.config.axis ?? 'y') {
      case 'x':
        this.mesh.rotation.x = this.startRotation.x + angle;
        break;
      case 'z':
        this.mesh.rotation.z = this.startRotation.z + angle;
        break;
      default: // y
        this.mesh.rotation.y = this.startRotation.y + angle;
        break;
    }
  }

  detach(): void {
    for (const inst of this.spikeInstances) {
      this.level?.player?.unregisterCollisionMesh(inst);
      inst.dispose();
    }
    this.spikeInstances = [];
    super.detach();
  }

  clone(): ObstacleSpikeRingBehavior {
    return new ObstacleSpikeRingBehavior(this.startPosition, this.pathIndex, this.config);
  }
}
